import { useEffect } from "react";

/*
  Seo — dependency-free document <head> manager for the public pages.

  It fetches the merged SEO settings for a given page slug from the backend
  (GET /api/seo/{slug}), which combines the global default with any per-page
  overrides the owner has set in the Super Admin "SEO & Sharing" manager. It
  then imperatively sets the title, meta description, canonical, Open Graph /
  Twitter cards, robots directive, and injects Organization + WebSite JSON-LD.

  Sensible fallbacks are baked in so the page still has good SEO if the request
  fails for any reason.
*/

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FALLBACKS = {
  home: {
    title: "Intrinsic Daily — Grow with intention. Live each day on purpose.",
    description:
      "A personal operating system for intentional living. Start with the Sunday Reset newsletter or take the free Life Audit.",
    canonical: "https://intrinsicdaily.com/",
  },
  "sunday-reset": {
    title: "The Sunday Reset — a weekly letter for intentional Christian women | Intrinsic Daily",
    description:
      "A warm, Scripture-rooted Sunday letter to help you begin each week grounded, clear, and on purpose. Free to join.",
    canonical: "https://intrinsicdaily.com/sunday-reset",
  },
  "life-audit": {
    title: "The Free Life Audit — one honest look across five pillars | Intrinsic Daily",
    description:
      "A free 10-minute reflection for Christian women across five pillars of life. Ten minutes. Five pillars. One honest score.",
    canonical: "https://intrinsicdaily.com/life-audit",
  },
};

const SITE_NAME = "Intrinsic Daily";
const SITE_URL = "https://intrinsicdaily.com";

function setMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  if (!href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function apply(seo) {
  const title = seo.title || SITE_NAME;
  document.title = title;

  setMeta("name", "description", seo.description);
  setMeta("name", "robots", seo.noindex ? "noindex, nofollow" : "index, follow");

  // Open Graph
  setMeta("property", "og:type", "website");
  setMeta("property", "og:site_name", seo.site_name || SITE_NAME);
  setMeta("property", "og:title", seo.social_title || title);
  setMeta("property", "og:description", seo.social_description || seo.description);
  setMeta("property", "og:url", seo.canonical);
  if (seo.image_url) {
    setMeta("property", "og:image", seo.image_url);
    setMeta("property", "og:image:alt", seo.image_alt || title);
  }

  // Twitter
  setMeta("name", "twitter:card", seo.image_url ? "summary_large_image" : "summary");
  setMeta("name", "twitter:title", seo.social_title || title);
  setMeta("name", "twitter:description", seo.social_description || seo.description);
  if (seo.image_url) setMeta("name", "twitter:image", seo.image_url);

  setCanonical(seo.canonical);

  // Structured data
  setJsonLd("ld-organization", {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: seo.site_name || SITE_NAME,
    url: seo.site_url || SITE_URL,
    description: seo.description || undefined,
    ...(seo.image_url ? { logo: seo.image_url } : {}),
  });
  setJsonLd("ld-website", {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: seo.site_name || SITE_NAME,
    url: seo.site_url || SITE_URL,
  });
}

export const Seo = ({ slug = "home" }) => {
  useEffect(() => {
    let cancelled = false;

    // Apply fallback immediately so tags are never empty.
    const fb = FALLBACKS[slug] || FALLBACKS.home;
    apply({
      site_name: SITE_NAME,
      site_url: SITE_URL,
      title: fb.title,
      description: fb.description,
      social_title: SITE_NAME,
      social_description: fb.description,
      canonical: fb.canonical,
      noindex: false,
    });

    // Then fetch the live, admin-managed values and re-apply.
    fetch(`${BACKEND}/api/seo/${slug}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data) apply(data);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [slug]);

  return null;
};

export default Seo;
